import React, { useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import moment from "moment";

import { Card } from "@cofano/cck";

import { CircularProgress, Grid, Switch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import TimelineIcon from "@mui/icons-material/Timeline";

import { request } from "components/utils/Request2";
import useUserPreferences from "components/hooks/useUserPreferences";
import { CustomDialog } from "components/dialogs/Dialogs";
import { useConfirm } from "components/hooks/useConfirm";

import { DataFormat } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import { WaterLevel } from "types/index";

import "./styling.scss";

type WaterLevelListProps = {
	personalLevels: WaterLevel[];
	refetch: () => void;
	disableBreakPoints?: boolean,
};

type WaterLevelItemProps = {
	waterLevel: WaterLevel;
	isDefault: boolean;
	onChangeDefault: (waterLevel: WaterLevel, checked: boolean) => void;
	onDelete: (waterLevel: WaterLevel) => void;
};

const WaterLevelList = ({ personalLevels, refetch, disableBreakPoints }: WaterLevelListProps) => {

	const user = useStoreState(state => state.session.user);
	const saveUserPreferences = useUserPreferences();

	const [ defaultWaterLevel, setDefaultWaterLevel ] = useState(user?.properties.userPreferences?.defaultWaterLevel);
	const [ loading, setloading ] = useState(false);

	const onChangeDefault = (waterLevel: WaterLevel, checked: boolean) => {
		setDefaultWaterLevel(checked ? waterLevel.id : undefined);

		void saveUserPreferences({
			preferences: {
				"defaultWaterLevel": checked ? waterLevel.id : undefined,
			},
			successCallback: undefined,
			errorCallback: undefined,
		});
	};

	const onDelete = (waterLevel: WaterLevel) => {

		setloading(true);

		request({
			method: "POST",
			body: [ ...personalLevels ].filter(it => it.id !== waterLevel.id).map(it => it.id),
			url: "waterlevels/subscriptions/personal",
		}).then(() => {
			refetch();
		}).finally(() => {
			setloading(false);
		});
	};

	return (
		<React.Fragment>
			{loading ? (
				<CircularProgress size={24} />
			) : (
				<Grid container spacing={3}>
					{personalLevels.map((it, index) => (
						<Grid
							item
							xs={12}
							sm={disableBreakPoints ? 12 : 6}
							md={disableBreakPoints ? 12 : 6}
							lg={disableBreakPoints ? 12 : 4}
							xl={disableBreakPoints ? 12 : 3}
							key={index}>
							<WaterLevelItem
								waterLevel={it}
								isDefault={defaultWaterLevel === it.id}
								onChangeDefault={onChangeDefault}
								onDelete={onDelete} />
						</Grid>
					))}
				</Grid>
			)}

		</React.Fragment>
	);
};

const WaterLevelItem = React.memo(({ waterLevel, isDefault, onChangeDefault, onDelete }: WaterLevelItemProps) => {

	const confirm = useConfirm();

	const [ chartDialogOpen, openChartDialog ] = useState<boolean>(false);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ chartData, setChartData ] = useState<Array<[ Date | object, string | number, boolean | object]>>([]);

	useEffect(() => {
		if (chartDialogOpen) {
			setLoading(true);

			const from = moment().add(-24, "hour").unix();
			const to = moment().add(14, "day").unix();

			void request<{ measurements: Array<WaterLevel> }>({
				url: `waterlevels/measurements/${waterLevel.id}?from=${from}&to=${to}`,
			}).then((response) => {

				const data: React.SetStateAction<[Date | object, string | number, boolean | object ][]> = [
					[
						{ type: "date", label: "Day" },
						"Waterlevel",
						{ role: "certainty" },
					],
				];

				response.measurements.forEach(it => {
					data.push([ new Date(it.timestamp * 1000), it.waterLevelCm,  !it.predicted ]);
				});

				setChartData(data);
			}).finally(() => {
				setLoading(false);
			});
		}
	}, [ chartDialogOpen ]);


	const options = {
		legend: { position: "none" },
	};

	return (
		<div id={`waterlevel-${waterLevel.id}`}>
			<Card
				title={waterLevel.name}
				subtitle={<FormattedDate date={waterLevel.timestamp} withoutYear />}
				actions={[ {
					onClick: () => openChartDialog(true),
					icon: <TimelineIcon />,
				}, {
					onClick: () => {
						confirm({
							type: "DELETE",
						}).then(() => {
							onDelete(waterLevel);
						});
					},
					icon: <DeleteIcon />,
				} ]}>
				<div className="waterlevel">
					<span className="level-cm">
						{DataFormat.waterLevel(waterLevel.waterLevelCm)}
					</span>

					<Switch
						checked={isDefault}
						onChange={event => {
							onChangeDefault(waterLevel, event.target.checked);
						}} />
				</div>
			</Card>

			<CustomDialog
				open={chartDialogOpen}
				onClose={() => openChartDialog(false)}
				title={intl.formatMessage({ id: "WATERLEVEL" })}
				maxWidth="lg">
				<React.Fragment>
					{loading ? (
						<CircularProgress size={24} />
					) : (
						<Chart
							chartType="LineChart"
							width="100%"
							height="500px"
							data={chartData}
							options={options} />
					)}
				</React.Fragment>
			</CustomDialog>
		</div>
	);
});

export default WaterLevelList;
